import { Box, Fade, Flex, Slide, Stack, useBreakpointValue, useDisclosure } from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { useNavigate } from 'react-router-dom'

import Logo from 'assets/logo-mono.svg'
import { MenuLayout } from 'components/layout/layout'
import NotificationBadge from 'components/notification-badge'
import Separator from 'components/separator'
import { useUserContext } from 'contexts/user-context'

import theme from 'theme'

import { ClubMenu, UserMenu } from '.'

type Props = {
  isSimple?: boolean
  onBack?: () => void
}

const transitionProps = {
  enter: { duration: 0.3, ease: 'easeInOut' },
  exit: { duration: 0.3, ease: 'easeInOut' },
}
const transitionInnerProps = {
  enter: { duration: 0.6, ease: 'easeInOut' },
  exit: { duration: 0.15, ease: 'easeInOut' },
}

const PageHeader = ({ isSimple, onBack }: Props) => {
  const navigate = useNavigate()
  const { isOpen: isOpenMenu, onToggle: onToggleMenu } = useDisclosure()
  const { shouldNotifyNewVersion: hasNotification } = useUserContext()
  const isSmallViewport = useBreakpointValue({ base: true, lg: false })

  const navigateToHome = () => {
    navigate('/')
    if (isOpenMenu) {
      onToggleMenu()
    }
  }

  return (
    <Flex w="100%" h="100%" align="center">
      <Flex
        flex={1}
        px={4}
        h="100%"
        minW={{ base: '268px', xl: '316px' }}
        align="flex-start"
        justify="space-between"
        background={theme.gradient.frogbox2}
      >
        <Flex
          w="100%"
          h={{ base: isSimple ? '100%' : 'auto', lg: '100%' }}
          align="center"
          {...(!isSimple && { mt: { base: '18px', lg: '0px' } })}
        >
          {isSmallViewport && !isSimple && (
            <Flex flex={1} align="flex-start" justify="center">
              {onBack && (
                <Flex
                  align="center"
                  justify="center"
                  w="66px"
                  h="32px"
                  borderRadius="26px"
                  backgroundColor={theme.colors.alpha.blackalpha['200']}
                  cursor="pointer"
                  onClick={onBack}
                  zIndex={theme.zIndex.menu}
                >
                  <Stack direction="row" spacing="8px">
                    <FontAwesomeIcon icon={['far', 'chevron-left']} size="sm" color={theme.colors.white} />
                    <FontAwesomeIcon icon={['far', 'house']} size="sm" color={theme.colors.white} />
                  </Stack>
                </Flex>
              )}
            </Flex>
          )}
          <Flex
            onClick={navigateToHome}
            flex={3}
            w="100%"
            align="center"
            justify="center"
            userSelect="none"
            zIndex={theme.zIndex.menu + 1}
            _hover={{ cursor: 'pointer' }}
          >
            <img src={Logo} alt="FrogBox logo" width="114px" height="20px" />
          </Flex>
          {isSmallViewport && !isSimple && (
            <Flex flex={1} align="flex-end" justify="flex-end">
              <Flex
                align="center"
                justify="center"
                boxSize="48px"
                borderRadius="12px"
                {...(hasNotification && !isOpenMenu && { pl: '12px' })}
                backgroundColor={theme.colors.alpha.blackalpha['200']}
                cursor="pointer"
                onClick={onToggleMenu}
                zIndex={theme.zIndex.menu + 1}
              >
                <FontAwesomeIcon icon={['far', isOpenMenu ? 'xmark' : 'bars']} size="xl" color={theme.colors.white} />
                {hasNotification && !isOpenMenu && (
                  <Box mt="-16px">
                    <NotificationBadge size="md" />
                  </Box>
                )}
              </Flex>
            </Flex>
          )}
        </Flex>
      </Flex>
      {!isSimple && (
        <>
          {isSmallViewport ? (
            // Mobile: Slide-in hamburger menu
            <Slide
              direction="right"
              in={isOpenMenu}
              transition={transitionProps}
              style={{ width: '100%', zIndex: theme.zIndex.menu }}
            >
              <Flex background={theme.gradient.frogbox2} align="flex-start" h="100vh" w="100%">
                <Fade style={{ width: '100%' }} in={isOpenMenu} transition={transitionInnerProps}>
                  <MenuLayout isMobile onToggleMenu={onToggleMenu} />
                </Fade>
              </Flex>
            </Slide>
          ) : (
            // Desktop: Header bar
            <Flex
              flex={5}
              justify="flex-end"
              pl={4}
              h="100%"
              background={theme.baseColors.background.default}
              borderBottom={`solid 1px ${theme.colors.gray['200']}`}
            >
              <Separator type="vertical" size="100%" />
              <ClubMenu />
              <Separator type="vertical" size="100%" />
              <UserMenu />
            </Flex>
          )}
        </>
      )}
    </Flex>
  )
}

export default PageHeader
